import { useContext, useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { gql, useQuery } from '@apollo/client';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { AppContext } from '@edx/frontend-platform/react';

// Friendly fallback label per component type (used when no real title resolves).
const TYPE_LABEL = {
  course: 'Course',
  video: 'Video',
  assessment: 'Assessment',
  text: 'Reading',
  material: 'Material',
  live_session: 'Live session',
  feedback: 'Feedback',
  role_play: 'Role play',
};

// Resolve library-block display names via blendxapi (modulestore-backed, so it
// works for learners who aren't staff on the competency library course).
const fetchComponentNames = async (blockIds) => {
  if (!blockIds.length) { return {}; }
  const { data } = await getAuthenticatedHttpClient().post(
    `${getConfig().LMS_BASE_URL}/blendxapi/learning-pathway/component-names/`,
    { block_ids: blockIds },
    { headers: { 'content-type': 'application/json' } },
  );
  return data?.names || {};
};

const GET_PATHWAY_BY_ID = gql`
  query FetchPathwayById($id: Uuid!, $userId: Int4!) {
    learningPathways(where: { id: { _eq: $id } }) {
      id
      name
      description
      image
      isSequential
      duration
      durationMetric
      learningPathwayComponents(order_by: { sequenceOrder: Asc }) {
        id
        contentId
        componentType
        isMandatory
        sequenceOrder
        learningPathwayComponentProgresses(where: { userId: { _eq: $userId } }) {
          completionStatus
          completionPercentage
        }
      }
    }
  }
`;

// Course title + image for course components, via the learner-accessible
// _CourseOverviewsCourseoverview relationship (same path the discover page uses).
const GET_PATHWAY_COURSE_DETAILS = gql`
  query FetchPathwayCourseDetails($ids: [Text!]!) {
    courses(where: { openedxCourseId: { _in: $ids } }) {
      openedxCourseId
      details: _CourseOverviewsCourseoverview {
        displayName
        courseImageUrl
      }
    }
  }
`;

/**
 * Compute per-component access state, honouring the pathway's is_sequential flag.
 * is_sequential=false -> nothing locked. is_sequential=true -> a component locks
 * once an earlier mandatory one is incomplete; optional components never gate.
 */
export const computeComponentStates = (components, isSequential) => {
  let blocked = false;
  return components.map((c) => {
    const progress = c.learningPathwayComponentProgresses?.[0];
    const isCompleted = progress?.completionStatus === 'completed';
    const percent = Number(progress?.completionPercentage ?? 0);

    let status;
    if (isCompleted) {
      status = 'completed';
    } else if (isSequential && blocked) {
      status = 'locked';
    } else if (percent > 0) {
      status = 'in_progress';
    } else {
      status = 'available';
    }

    if (isSequential && c.isMandatory && !isCompleted) {
      blocked = true;
    }

    return {
      ...c, percent, isCompleted, status, isLocked: status === 'locked',
    };
  });
};

export const usePathwayById = (id) => {
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId;

  const { data, loading, error } = useQuery(GET_PATHWAY_BY_ID, {
    variables: { id, userId },
    skip: !id || !userId,
    fetchPolicy: 'cache-and-network',
  });

  const pathway = data?.learningPathways?.[0] || null;

  const baseComponents = useMemo(
    () => computeComponentStates(
      pathway?.learningPathwayComponents ?? [],
      Boolean(pathway?.isSequential),
    ),
    [pathway],
  );

  // Course titles + images (GraphQL).
  const courseIds = useMemo(
    () => baseComponents.filter((c) => c.componentType === 'course').map((c) => c.contentId),
    [baseComponents],
  );
  const { data: courseData } = useQuery(GET_PATHWAY_COURSE_DETAILS, {
    variables: { ids: courseIds },
    skip: !courseIds.length,
    fetchPolicy: 'cache-and-network',
  });
  const courseDetailById = useMemo(() => {
    const map = {};
    (courseData?.courses ?? []).forEach((c) => {
      // eslint-disable-next-line no-underscore-dangle
      const d = c.details;
      map[c.openedxCourseId] = { title: d?.displayName || null, image: d?.courseImageUrl || null };
    });
    return map;
  }, [courseData]);

  // Library-block names via blendxapi (modulestore-backed).
  const blockIds = useMemo(() => [...new Set(
    baseComponents
      .filter((c) => c.componentType !== 'course')
      .map((c) => c.contentId),
  )].sort(), [baseComponents]);
  const { data: blockNames = {} } = useReactQuery({
    queryKey: ['pathwayBlockNames', blockIds],
    queryFn: () => fetchComponentNames(blockIds),
    enabled: blockIds.length > 0,
  });

  const components = useMemo(() => baseComponents.map((c) => {
    const courseDetail = courseDetailById[c.contentId];
    return {
      ...c,
      displayName: courseDetail?.title
        || blockNames[c.contentId]
        || TYPE_LABEL[c.componentType]
        || c.contentId,
      imageUrl: courseDetail?.image || null,
    };
  }), [baseComponents, courseDetailById, blockNames]);

  const completedCount = components.filter((c) => c.isCompleted).length;
  const progressPercent = components.length
    ? Math.round((completedCount / components.length) * 100)
    : 0;
  const resumeComponent = components.find((c) => !c.isCompleted && !c.isLocked) || null;

  return {
    pathway,
    components,
    progressPercent,
    completedCount,
    resumeComponent,
    loading,
    error,
  };
};
